import React, { useEffect, useState } from "react";
import { Palette, Plus, Trash2, ExternalLink, LogOut, Upload, Check, ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const IMAGE_KINDS = [
  { key: "logo", label: "Logo", hint: "PNG, JPG, WEBP oder SVG · max. 2 MB" },
  { key: "favicon", label: "Favicon", hint: "PNG, JPG, ICO oder SVG · max. 2 MB" },
  { key: "background", label: "Login-Hintergrundbild", hint: "PNG, JPG oder WEBP · max. 5 MB" },
];

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-overline text-white/50 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}

export default function BrandingStudioPage() {
  const [profiles, setProfiles] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const load = async (keepSelection = true) => {
    try {
      const { data } = await api.get("/branding/profiles");
      setProfiles(data);
      if (!keepSelection && data.length) setSelectedId(data[0].profile_id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  useEffect(() => { load(false); }, []);

  useEffect(() => {
    const p = (profiles || []).find((x) => x.profile_id === selectedId);
    setForm(p ? { ...p } : null);
  }, [selectedId, profiles]);

  const createProfile = async () => {
    if (!newName.trim()) return toast.error("Bitte einen internen Namen angeben");
    setCreating(true);
    try {
      const { data } = await api.post("/branding/profiles", { name: newName.trim() });
      setNewName("");
      await load();
      setSelectedId(data.profile_id);
      toast.success("Branding-Profil angelegt");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setCreating(false); }
  };

  const saveProfile = async () => {
    if (!form) return;
    setSaving(true);
    try {
      await api.put(`/branding/profiles/${form.profile_id}`, {
        name: form.name, company_name: form.company_name, footer_text: form.footer_text,
        primary_color: form.primary_color, accent_color: form.accent_color,
      });
      await load();
      toast.success("Gespeichert");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const deleteProfile = async (profileId) => {
    if (!window.confirm("Dieses Branding-Profil wirklich löschen?")) return;
    try {
      await api.delete(`/branding/profiles/${profileId}`);
      if (selectedId === profileId) setSelectedId(null);
      await load();
      toast.success("Gelöscht");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const uploadImage = async (kind, file) => {
    if (!file || !form) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/branding/profiles/${form.profile_id}/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
      toast.success(`${kind === "logo" ? "Logo" : kind === "favicon" ? "Favicon" : "Hintergrundbild"} hochgeladen`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const { user: authUser } = useAuth();
  const isStaffUser = authUser && authUser.role !== "designer" && authUser.role !== "client" && authUser.role !== "participant";
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* noop */ }
    window.location.href = "/login";
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col" data-testid="branding-studio-page">
      <div className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Palette size={16} strokeWidth={1.5} className="text-[#00CCC9]" />
          <div>
            <div className="text-[10px] font-bold uppercase tracking-overline text-white/50">Designer-Zugang</div>
            <div className="font-heading text-lg font-light tracking-tighter -mt-0.5">Branding Studio</div>
          </div>
        </div>
        {isStaffUser ? (
          <a href="/dashboard" data-testid="branding-back-portal" className="text-xs text-white/60 hover:text-white flex items-center gap-1.5">
            <ArrowLeft size={13} strokeWidth={1.5} /> Zurück zum Portal
          </a>
        ) : <button onClick={logout} data-testid="designer-logout-btn"
          className="text-xs text-white/60 hover:text-white flex items-center gap-1.5">
          <LogOut size={13} strokeWidth={1.5} /> Abmelden
        </button>}
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[280px_1fr]">
        {/* Left: profile list */}
        <div className="border-r border-white/10 p-4 flex flex-col gap-1" data-testid="branding-profile-list">
          <div className="flex gap-1.5 mb-3">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && createProfile()}
              placeholder="Neues Profil (interner Name)"
              className="flex-1 bg-white/5 border border-white/15 px-2.5 py-2 text-xs text-white placeholder:text-white/30 outline-none focus:border-[#00CCC9]"
              data-testid="branding-new-profile-input"
            />
            <button onClick={createProfile} disabled={creating} data-testid="branding-new-profile-btn"
              className="px-2.5 bg-[#00CCC9] text-[#0A0A0A] hover:brightness-105 disabled:opacity-50">
              <Plus size={14} strokeWidth={2} />
            </button>
          </div>
          {!profiles ? (
            <div className="text-xs text-white/40 italic py-6 text-center">Lädt…</div>
          ) : profiles.length === 0 ? (
            <div className="text-xs text-white/40 italic py-6 text-center">Noch keine Profile angelegt.</div>
          ) : (
            profiles.map((p) => (
              <button key={p.profile_id} onClick={() => setSelectedId(p.profile_id)} data-testid={`branding-profile-row-${p.profile_id}`}
                className={`text-left px-3 py-2.5 flex items-center gap-2.5 border ${selectedId === p.profile_id ? "border-[#00CCC9] bg-white/5" : "border-transparent hover:bg-white/5"}`}>
                <span className="w-3 h-3 rounded-full shrink-0 border border-white/20" style={{ background: p.primary_color }} />
                <span className="flex-1 text-sm truncate">{p.name}</span>
              </button>
            ))
          )}
        </div>

        {/* Right: editor */}
        <div className="p-8" data-testid="branding-editor-panel">
          {!form ? (
            <div className="text-sm text-white/40 italic text-center py-20">
              Wähle links ein Profil aus oder lege ein neues an.
            </div>
          ) : (
            <div className="max-w-xl space-y-6">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-overline text-white/50">Profil</div>
                  <div className="font-heading text-2xl font-light tracking-tighter">{form.name}</div>
                </div>
                <div className="flex items-center gap-3">
                  <a href={`/brand-preview/${form.profile_id}`} target="_blank" rel="noopener noreferrer"
                    data-testid="branding-preview-link"
                    className="text-xs text-[#00CCC9] hover:underline flex items-center gap-1.5">
                    Vorschau öffnen <ExternalLink size={12} strokeWidth={1.5} />
                  </a>
                  <button onClick={() => deleteProfile(form.profile_id)} data-testid="branding-delete-btn"
                    className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1.5">
                    <Trash2 size={12} strokeWidth={1.5} /> Löschen
                  </button>
                </div>
              </div>

              <Field label="Firmenname (sichtbar für Kund:innen)">
                <input value={form.company_name || ""} onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  className="w-full bg-white/5 border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#00CCC9]"
                  data-testid="branding-company-name-input" placeholder="z.B. Mueller Coaching GmbH" />
              </Field>

              <Field label="Footer-/Impressum-Text (E-Mails & Login)">
                <textarea value={form.footer_text || ""} onChange={(e) => setForm({ ...form, footer_text: e.target.value })}
                  rows={3} className="w-full bg-white/5 border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#00CCC9] resize-none"
                  data-testid="branding-footer-text-input" placeholder="Firmenname · Adresse · Kontakt" />
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Primärfarbe">
                  <div className="flex items-center gap-2">
                    <input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                      className="w-10 h-10 bg-transparent border border-white/15 cursor-pointer" data-testid="branding-primary-color-input" />
                    <span className="font-mono text-xs text-white/60">{form.primary_color}</span>
                  </div>
                </Field>
                <Field label="Akzentfarbe">
                  <div className="flex items-center gap-2">
                    <input type="color" value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                      className="w-10 h-10 bg-transparent border border-white/15 cursor-pointer" data-testid="branding-accent-color-input" />
                    <span className="font-mono text-xs text-white/60">{form.accent_color}</span>
                  </div>
                </Field>
              </div>

              <button onClick={saveProfile} disabled={saving} data-testid="branding-save-btn"
                className="px-5 py-2.5 bg-[#00CCC9] text-[#0A0A0A] text-sm font-semibold hover:brightness-105 disabled:opacity-50 flex items-center gap-2">
                <Check size={14} strokeWidth={2} /> {saving ? "Speichert…" : "Speichern"}
              </button>

              <div className="pt-4 border-t border-white/10 space-y-4">
                {IMAGE_KINDS.map(({ key, label, hint }) => (
                  <div key={key} className="flex items-center gap-4" data-testid={`branding-upload-row-${key}`}>
                    <div className="w-16 h-16 border border-white/15 bg-white/5 flex items-center justify-center shrink-0 overflow-hidden">
                      {form[`has_${key}`] ? (
                        <img src={`${API}/branding/profiles/${form.profile_id}/image/${key}`} alt={label} className="w-full h-full object-contain p-1" />
                      ) : (
                        <Upload size={16} strokeWidth={1.5} className="text-white/25" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm">{label}</div>
                      <div className="text-[10px] text-white/40">{hint}</div>
                    </div>
                    <label className="text-xs border border-white/20 hover:bg-white/10 px-3 py-2 cursor-pointer" data-testid={`branding-upload-btn-${key}`}>
                      Hochladen
                      <input type="file" accept="image/*,.ico" className="hidden"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadImage(key, f); e.target.value = ""; }} />
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
